/**
 * MachineFormDialog.tsx
 * Add / Edit dialog for the Machines page (Phase 1, Item 5).
 * Phase 5.2: Added per-capability Machine Minutes input.
 *
 * Fields:
 *   Code        — short code, e.g. M-01
 *   Name        — descriptive name
 *   LabSection  — select from labSectionOptions constant
 *   Status      — select from machineStatusOptions constant
 *   TaskTypes   — MUI Autocomplete multi-select (task type list from props)
 *   Machine Minutes — per-selected task type, integer ≥ 0 (Phase 5.2)
 *   Notes       — optional free-text
 */

import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { machineStatusOptions, machineStatusLabels, labSectionOptions } from '../../../constants/statusOptions';
import type {
  MachineRow,
  MachineTaskTypeRef,
  CreateMachinePayload,
  UpdateMachinePayload,
} from '../../../services/scheduleMachines';
import type { LabSection, MachineStatus } from '../../../types/schedule';
// Run Mode Phase A: per-mode minutes semantics helper text.
import { minutesSemanticsHint } from '../../../utils/runMode';

interface MachineFormDialogProps {
  open: boolean;
  /** Null = create mode; non-null = edit mode */
  machine: MachineRow | null;
  /** Full task-type list to drive the multi-select */
  allTaskTypes: MachineTaskTypeRef[];
  onClose: () => void;
  onSave: (data: CreateMachinePayload | UpdateMachinePayload) => Promise<void>;
}

interface FormState {
  Code: string;
  Name: string;
  LabSection: LabSection | '';
  /** Migration 120: test-family group under the lab section (free text). */
  MachineGroup: string;
  Status: MachineStatus;
  Notes: string;
  selectedTaskTypes: MachineTaskTypeRef[];
  /**
   * Phase 5.2: per-TaskTypeKey machine minutes (stored as string for TextField).
   * Empty string = NULL (no minutes set).
   * Keys are TaskTypeKey UUIDs.
   */
  minutesMap: Record<string, string>;
}

const DEFAULT_FORM: FormState = {
  Code: '',
  Name: '',
  LabSection: '',
  MachineGroup: '',
  Status: 'Active',
  Notes: '',
  selectedTaskTypes: [],
  minutesMap: {},
};

/** Parse a minutes string: returns a non-negative integer or null. */
function parseMinutes(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseInt(raw, 10);
  if (isNaN(n) || n < 0) return null;
  return n;
}

/** Returns true when the minutes string is valid (empty or non-negative integer). */
function isValidMinutes(raw: string): boolean {
  if (!raw.trim()) return true; // empty = null = valid
  const n = parseInt(raw, 10);
  return !isNaN(n) && n >= 0 && String(n) === raw.trim();
}

export const MachineFormDialog: React.FC<MachineFormDialogProps> = ({
  open,
  machine,
  allTaskTypes,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form from machine when editing
  useEffect(() => {
    if (open) {
      if (machine) {
        // Build a minutesMap from the existing TaskTypes (with MachineMinutes — Phase 5.2)
        const initialMinutesMap: Record<string, string> = {};
        for (const tt of machine.TaskTypes) {
          initialMinutesMap[tt.TaskTypeKey] =
            tt.MachineMinutes != null ? String(tt.MachineMinutes) : '';
        }
        setForm({
          Code: machine.Code,
          Name: machine.Name,
          LabSection: machine.LabSection ?? '',
          MachineGroup: machine.MachineGroup ?? '',
          Status: machine.Status,
          Notes: machine.Notes ?? '',
          selectedTaskTypes: machine.TaskTypes,
          minutesMap: initialMinutesMap,
        });
      } else {
        setForm(DEFAULT_FORM);
      }
      setError(null);
    }
  }, [open, machine]);

  const handleSave = async () => {
    if (!form.Code.trim()) { setError('Code is required'); return; }
    if (!form.Name.trim()) { setError('Name is required'); return; }

    // Validate all minutes fields
    for (const tt of form.selectedTaskTypes) {
      const raw = form.minutesMap[tt.TaskTypeKey] ?? '';
      if (!isValidMinutes(raw)) {
        setError(`Machine Minutes for "${tt.Name}" must be a whole number ≥ 0 (or leave blank).`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      // Phase 5.2: send TaskTypeCapabilities (key + minutes) instead of bare TaskTypeKeys
      const taskTypeCapabilities = form.selectedTaskTypes.map((tt) => ({
        TaskTypeKey: tt.TaskTypeKey,
        MachineMinutes: parseMinutes(form.minutesMap[tt.TaskTypeKey] ?? ''),
      }));

      const payload: CreateMachinePayload | UpdateMachinePayload = {
        Code: form.Code.trim(),
        Name: form.Name.trim(),
        LabSection: (form.LabSection || undefined) as LabSection | undefined,
        MachineGroup: form.MachineGroup.trim() || null,
        Status: form.Status,
        Notes: form.Notes.trim() || undefined,
        TaskTypeCapabilities: taskTypeCapabilities,
      };
      await onSave(payload);
      onClose();
    } catch (e: unknown) {
      // Phase 5.7: prefer the API's error body (e.g. "LIVE_BOOKING_GUARD" 409 message)
      const axiosMsg =
        (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      const msg = axiosMsg ?? (e instanceof Error ? e.message : 'Save failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLabSectionChange = (e: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, LabSection: e.target.value as LabSection | '' }));
  };

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, Status: e.target.value as MachineStatus }));
  };

  /** When the multi-select changes, keep existing minutes and clear removed ones. */
  const handleTaskTypesChange = (_e: React.SyntheticEvent, value: MachineTaskTypeRef[]) => {
    const nextKeys = new Set(value.map((tt) => tt.TaskTypeKey));
    setForm((prev) => {
      // Keep minutes for retained task types; discard minutes for removed ones
      const nextMinutesMap: Record<string, string> = {};
      for (const key of nextKeys) {
        nextMinutesMap[key] = prev.minutesMap[key] ?? '';
      }
      return { ...prev, selectedTaskTypes: value, minutesMap: nextMinutesMap };
    });
  };

  const handleMinutesChange = (taskTypeKey: string, value: string) => {
    // Accept only digits (allow empty)
    if (value !== '' && !/^\d+$/.test(value)) return;
    setForm((prev) => ({
      ...prev,
      minutesMap: { ...prev.minutesMap, [taskTypeKey]: value },
    }));
  };

  const isEdit = machine !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {isEdit ? 'Edit Machine' : 'Add Machine'}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && (
          <Typography variant="body2" color="error.main" sx={{ mb: 0 }}>
            {error}
          </Typography>
        )}

        {/* Code */}
        <TextField
          label="Code"
          size="small"
          value={form.Code}
          onChange={(e) => setForm((prev) => ({ ...prev, Code: e.target.value }))}
          placeholder="e.g. M-01"
          required
          fullWidth
          inputProps={{ maxLength: 20 }}
        />

        {/* Name */}
        <TextField
          label="Name"
          size="small"
          value={form.Name}
          onChange={(e) => setForm((prev) => ({ ...prev, Name: e.target.value }))}
          placeholder="e.g. Rock Frame 1"
          required
          fullWidth
          inputProps={{ maxLength: 250 }}
        />

        {/* Lab Section */}
        <FormControl size="small" fullWidth>
          <InputLabel>Lab Section</InputLabel>
          <Select
            label="Lab Section"
            value={form.LabSection}
            onChange={handleLabSectionChange}
          >
            <MenuItem value=""><em>— None —</em></MenuItem>
            {labSectionOptions.map((ls) => (
              <MenuItem key={ls} value={ls}>{ls}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Machine Group (migration 120) — the second grouping level: the test
            family this machine serves, shown under the Lab Section in the
            Machine Schedule / Phase-1 grids. Free text so the lab owns the
            taxonomy (e.g. "Triaxial", "DSS / CDSS / DRS", "Oedometer / Consolidation"). */}
        <TextField
          label="Machine Group"
          size="small"
          value={form.MachineGroup}
          onChange={(e) => setForm((prev) => ({ ...prev, MachineGroup: e.target.value }))}
          fullWidth
          inputProps={{ maxLength: 100 }}
          placeholder="e.g. Triaxial, DSS / CDSS / DRS, Oedometer / Consolidation"
          helperText="Groups this machine under its lab section in the schedule views. Leave blank to group by the machine name."
        />

        {/* Status */}
        <FormControl size="small" fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={form.Status}
            onChange={handleStatusChange}
          >
            {machineStatusOptions.map((s) => (
              <MenuItem key={s} value={s}>{machineStatusLabels[s] ?? s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Task Types multi-select */}
        <Autocomplete
          multiple
          size="small"
          options={allTaskTypes}
          getOptionLabel={(opt) => opt.Name}
          value={form.selectedTaskTypes}
          onChange={handleTaskTypesChange}
          isOptionEqualToValue={(opt, val) => opt.TaskTypeKey === val.TaskTypeKey}
          renderInput={(params) => (
            <TextField {...params} label="Task Types (capabilities)" placeholder="Select..." />
          )}
          groupBy={(opt) => opt.Category ?? 'Other'}
          ChipProps={{ size: 'small' }}
        />

        {/* Phase 5.2: per-capability Machine Minutes inputs */}
        {form.selectedTaskTypes.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Machine Minutes per test (leave blank if unknown)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {form.selectedTaskTypes.map((tt) => {
                const raw = form.minutesMap[tt.TaskTypeKey] ?? '';
                const invalid = raw !== '' && !isValidMinutes(raw);
                return (
                  <Box
                    key={tt.TaskTypeKey}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={tt.Name}
                    >
                      {tt.Name}
                    </Typography>
                    <TextField
                      size="small"
                      label="Minutes"
                      value={raw}
                      onChange={(e) => handleMinutesChange(tt.TaskTypeKey, e.target.value)}
                      error={invalid}
                      helperText={
                        invalid
                          ? 'Must be a whole number ≥ 0'
                          // Run Mode Phase A: per-mode semantics ('attended'
                          // packs into 8h days; 'continuous' shows a live
                          // calendar-day preview of the 24/7 elapsed time).
                          : minutesSemanticsHint(tt.RunMode, parseMinutes(raw))
                      }
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5 }}
                      sx={{ width: 190 }}
                      placeholder="e.g. 30"
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Notes */}
        <TextField
          label="Notes"
          size="small"
          value={form.Notes}
          onChange={(e) => setForm((prev) => ({ ...prev, Notes: e.target.value }))}
          multiline
          rows={2}
          fullWidth
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Machine'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
